import { useUiStore } from '@/state/uiStore.js';
import Modal from './Modal.jsx';
import ConfirmMessage from './ConfirmMessage.jsx';

/**
 * Confirmation modal that mirrors the monolith's `showConfirmModal()`:
 *   showConfirmModal(title, message, confirmText, callback, type)
 *
 * Type → icon, header gradient, button colour (verbatim from monolith).
 */

const ICONS = {
  warning: 'fa-exclamation-triangle',
  danger: 'fa-exclamation-circle',
  success: 'fa-check-circle',
  info: 'fa-info-circle'
};

const HEADER_GRADIENTS = {
  warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  danger: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  info: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
};

const BUTTON_COLOURS = {
  warning: { bg: 'rgba(245,158,11,0.12)', hoverBg: 'rgba(245,158,11,0.2)', border: 'rgba(245,158,11,0.28)', color: '#b45309' },
  danger: { bg: 'rgba(239,68,68,0.1)', hoverBg: 'rgba(239,68,68,0.18)', border: 'rgba(239,68,68,0.22)', color: '#dc2626' },
  success: { bg: 'rgba(16,185,129,0.1)', hoverBg: 'rgba(16,185,129,0.18)', border: 'rgba(16,185,129,0.22)', color: '#047857' },
  info: { bg: 'rgba(59,130,246,0.1)', hoverBg: 'rgba(59,130,246,0.18)', border: 'rgba(59,130,246,0.22)', color: '#1d4ed8' }
};

/**
 * Renders the currently-open confirm modal from the UI store. Mount
 * this once at the app root and call `useConfirm()` from anywhere to
 * trigger it.
 */
export default function ConfirmModal() {
  const confirm = useUiStore((s) => s.confirm);
  const closeConfirm = useUiStore((s) => s.closeConfirm);

  if (!confirm) return null;

  const { title, message, confirmText, type, onConfirm } = confirm;
  const colours = BUTTON_COLOURS[type] ?? BUTTON_COLOURS.warning;

  function handleConfirm() {
    // Run callback BEFORE closing — matches monolith order so callers
    // that immediately open another modal don't race the close.
    try {
      onConfirm?.();
    } finally {
      closeConfirm();
    }
  }

  return (
    <Modal
      open
      id="confirmModal"
      className="modal--confirm"
      onClose={closeConfirm}
      title={title}
      titleIcon={ICONS[type] ?? ICONS.warning}
      headerStyle={{ background: HEADER_GRADIENTS[type] ?? HEADER_GRADIENTS.warning }}
      ariaLabel={title}
      width="480px"
      footer={
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={closeConfirm}
            style={{
              padding: '10px 22px',
              background: 'var(--hairline)',
              color: 'var(--text-2)',
              border: '1px solid var(--hairline-strong)',
              borderRadius: 999,
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(15,23,42,0.09)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--hairline)';
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            id="confirmModalButton"
            onClick={handleConfirm}
            style={{
              padding: '10px 22px',
              background: colours.bg,
              color: colours.color,
              border: `1px solid ${colours.border}`,
              borderRadius: 999,
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = colours.hoverBg;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = colours.bg;
            }}
          >
            {confirmText}
          </button>
        </div>
      }
    >
      {/* Callers pass the message as HTML (256 <strong>s and 88 <br>s across
          the call sites), so that contract is kept — but it is rendered
          through ConfirmMessage, which filters it against an allowlist and
          builds React elements rather than setting innerHTML.

          The old comment here claimed the message "is always authored by the
          app, not user input". Review finding H7: 94 of the 112 call sites
          interpolate a value from the server, and some of those — a file name
          on a remote device, a service or installed-app name, a registry
          value name — are free text a low-privileged person controls. */}
      <div id="confirmModalMessage">
        <ConfirmMessage message={message} />
      </div>
    </Modal>
  );
}
